<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-btn @click="resetFilters">
          <v-icon>mdi-refresh</v-icon>
          Reset Filter
        </v-btn>
        <v-btn @click="exportTableToCSV">
          <v-icon>mdi-export</v-icon>
          Export Results
        </v-btn>
      </v-col>
      <v-col cols="11">
        <v-card outlined>
          <v-card-text>
            <AdvancedFilter
              :filter-params="basicFilterParams"
              :updated-params="filterState"
              horizontal-toggler
              height
              @filter="filterComponentUpdate"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="1" class="flex-display">
        <v-row justify="center">
          <v-dialog width="65vw" :retain-focus="false">
            <template #activator="{ on }">
              <v-btn fab v-on="on">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>Advanced Filtering</v-card-title>
              <v-card outlined height="65vh" style="overflow-y: scroll">
                <v-card-text>
                  <AdvancedFilter :filter-params="blacklistedFilterParams" height @filter="filterComponentUpdate" />
                  <input v-model="metricTables.currentState" type="checkbox" @click="updateFilterState(filterState)" />
                  De-Aggregate
                </v-card-text>
              </v-card>
            </v-card>
          </v-dialog>
        </v-row>
      </v-col>
      <v-col v-for="v in pieCharts" :key="v.key" cols="6">
        <ChartGroup
          :id="v.key"
          ref="pieChart"
          :title="v.name"
          :source="v.chartGroupData"
          @change="chartGroupSelected"
        />
      </v-col>
    </v-row>
    <v-row v-if="filterState.length > 0">
      <v-col cols="12">
        <p>{{ displayFilterParams }}</p>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <AggregateTable
            v-if="metricTables.currentState === metricTables.currentStateEnum.aggregate"
            :filter="filter"
            @onDetailFieldClick="handleAggregateDetailFieldClick"
          />
          <NonAggregateTable v-else :filter="filter" @onDetailFieldClick="handleNonAggregateDetailFieldClick" />
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showReferringImages" max-width="1200" :retain-focus="false">
      <ReferringImagesTable :data="referringImagesData" @onClose="handleCloseReferringImagesTable" />
    </v-dialog>
  </v-container>
</template>

<script>
import MetricController from '@/controllers/MetricController';
import AdvancedFilter from '@/components/widgets/AdvancedFilter.vue';
import ChartGroup from '@/components/InspectionView/ChartGroup.vue';
import { ClientExtraEquipmentText } from '@/utils';
import { get, isNil } from 'lodash';
import ReferringImagesTable from './ReferringImagesTable.vue';
import { AggregateTable, NonAggregateTable } from './components';
import { exportTableAsCSV, formatAggregateFields, formatNonAggregateFields } from './utils';

export default {
  name: 'Equipment',
  components: {
    AdvancedFilter,
    ChartGroup,
    AggregateTable,
    NonAggregateTable,
    ReferringImagesTable,
  },
  props: {
    filterParams: {
      type: Object,
      default: () => ({}),
    },
    inspectionConfig: {
      type: Object,
      default: () => ({}),
    },
    extraEquipmentText: {
      type: String,
      default: ClientExtraEquipmentText.NONE,
    },
    initialFilters: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showReferringImages: false,
      referringImagesData: undefined,
      filter: undefined,
      pieCharts: {},
      activeChartQuery: null,
      tableRes: undefined,
      filterState: [],
      metricTables: {
        // 0 == aggregate, 1 == specific
        currentState: 0,
        currentStateEnum: {
          aggregate: 0,
          specific: 1,
        },
      },
      horizontalToggler: false,
    };
  },
  computed: {
    blacklistedFilterParams() {
      const a = { ...this.filterParams };
      delete a['meta.AutoCad:LineKey'];
      delete a['meta.AutoCad:Long Description'];
      return a;
    },
    displayFilterParams() {
      const corrosionStates = ['Clean', 'Light', 'Moderate', 'Heavy', 'Any'];
      const aggregated = this.metricTables.currentState === this.metricTables.currentStateEnum.aggregate;

      return this.filterState.reduce((display, { key, value }) => {
        if (isNil(value)) {
          return display;
        }
        if (key === 'metrics.corrosion_category') {
          return `${display} Corrosion Category - ${corrosionStates[value]}`;
        }
        return `${display} ${get(this, ['filterParams', key, 'displayName'], '')} - ${value}`;
      }, `Active Filter${aggregated ? ' (Aggregated)' : ''}:`);
    },
    basicFilterParams() {
      const result = {
        'metrics.corrosion_category': {
          displayName: 'Corrosion Category',
          options: ['Clean', 'Light', 'Moderate', 'Heavy', 'Any'],
        },
      };
      const basicParams = [
        'meta.AutoCad:Size',
        'meta.AutoCad:Service',
        'meta.AutoCad:Spec',
        'meta.AutoCad:LineKey',
        'meta.AutoCad:Class',
        'meta.AutoCad:Group',
      ];

      for (const param of basicParams) {
        if (param in this.filterParams) {
          result[param] = this.filterParams[param];
        }
      }
      return result;
    },
  },
  watch: {
    initialFilters(value) {
      this.updateFilterState(value);
    },
  },
  methods: {
    exportTableToCSV() {
      exportTableAsCSV(
        this.metricTables.currentState === this.metricTables.currentStateEnum.aggregate,
        this.tableRes,
        this.inspectionConfig.areaMetrics,
        this.inspectionConfig.distanceMetrics,
        this.$store.state.unit
      );
    },
    /**
     * @param {array} activeParams   updates to apply.
     * @param {array} clearParamKeys params to remove from state.
     * @param {bool}  reset          true - don't use existing filter.
     */
    async updateFilterState(activeParams, clearParamKeys, reset) {
      let filterState = [];
      if (!reset) {
        const newFilters = new Set(activeParams.map((p) => p.key));
        filterState = [...this.filterState.filter((p) => !newFilters.has(p.key)), ...activeParams];
        if (clearParamKeys !== undefined) {
          const clearParamKeysSet = new Set(clearParamKeys);
          filterState = [
            ...filterState.filter((p) => !clearParamKeysSet.has(p.key)),
            ...clearParamKeys.map((key) => ({ key, value: null })),
          ];
        }
      }
      this.filterState = filterState;
      this.updateTable(this.filterState);
    },
    /**
     handle event from AdvancedFilter
     * @param {array} activeParams   updates to apply.
     * @param {array} clearParamKeys params to remove from state.
     */
    async filterComponentUpdate(activeParams, clearedParamKeys) {
      this.updateFilterState(activeParams, clearedParamKeys, false);
    },
    handleCloseReferringImagesTable() {
      this.showReferringImages = false;
    },
    handleAggregateDetailFieldClick({ row }) {
      this.metricTables.currentState = this.metricTables.currentStateEnum.specific;
      this.updateFilterState([...this.filterState, { key: 'meta.AutoCad:LineKey', value: row.filterKey }]);
    },
    async handleNonAggregateDetailFieldClick({ row }) {
      this.showReferringImages = true;
      this.referringImagesData = row;
    },
    async updateTable(e) {
      this.filter = e;
      // reset table state
      // empty table res while table loading
      this.tableRes = undefined;
      await this.updateSearchAsset(e);
    },
    async chartGroupSelected(id, clickInfo) {
      const chart = this.pieCharts.find((f) => f && f.key === id);
      if (!chart) {
        return;
      }

      const getDefectFilters = (clickInfo) => {
        if (clickInfo.chart === 'detail') {
          return {
            addFilters: [{ key: 'metrics.corrosion_category', value: clickInfo.dataPoint + 1 }],
            clearFilters: ['metrics.remediated'],
          };
        }
        // data point 0 of 'summary' is corrosion category 'clean'
        switch (clickInfo.dataPoint) {
          case 0:
            return {
              addFilters: [{ key: 'metrics.corrosion_category', value: 0 }],
              clearFilters: ['metrics.remediated'],
            };
          case 1:
            // 'corroded' region clicked on 'corroded vs clean' chart
            // 4 is a special value for 'Any' - handled by API to for specific query
            return {
              addFilters: [{ key: 'metrics.corrosion_category', value: 4 }],
              clearFilters: ['metrics.remediated'],
            };
          case 2:
            // 'remediated' region clicked on 'corroded vs clean' chart
            return {
              addFilters: [{ key: 'metrics.remediated', value: 1 }],
              clearFilters: ['metrics.corrosion_category'],
            };
          default:
            throw new Error('unexpected chart section clicked');
        }
      };

      let chartState = null;
      let defectFilters = null;
      let assetFilters = null;
      if (chart.chartType === 'group') {
        chartState = this.metricTables.currentStateEnum.aggregate;
        defectFilters = getDefectFilters(clickInfo);
        assetFilters = {
          addFilters: [{ key: 'meta.AutoCad:Group', value: chart.groupKey }],
          clearFilters: ['meta.AutoCad:Class'],
        };
      } else {
        chartState = this.metricTables.currentStateEnum.specific;
        defectFilters = getDefectFilters(clickInfo);
        assetFilters = {
          addFilters: [{ key: 'meta.AutoCad:Class', value: id }],
          clearFilters: ['meta.AutoCad:Group'],
        };
      }
      if (defectFilters) {
        this.metricTables.currentState = chartState;
        this.updateFilterState(
          [...defectFilters.addFilters, ...assetFilters.addFilters],
          [...defectFilters.clearFilters, ...assetFilters.clearFilters]
        );
      }
      // else {
      //   if (this.$refs.pieChart) {
      //     this.$refs.pieChart.forEach(({ deSelectSegment }) => deSelectSegment());
      //   }
      // }
    },
    async updateSearchAsset(query) {
      if (query === undefined || query === 0) {
        return false;
      }

      const aggregationKey = '$data.meta.AutoCad:LineKey';
      // exclude corrosion and asset class filter from chart query
      // all other metadata filters are applied
      const chartsExcludeFilter = [
        'meta.AutoCad:Class',
        'meta.AutoCad:Group',
        'metrics.corrosion_category',
        'metrics.remediated',
      ];

      const lineChart = {
        name: 'Piping Condition',
        key: 'line',
        chartType: 'group',
        groupKey: 'Pipe',
        chartGroupData: [
          {
            name: 'Corroded vs Clean',
            series: [0, 0, 0],
            labels: ['Clean', 'Corroded', 'Remediated'],
            colors: ['#02B49C', '#F28705', '#799df8'],
          },
          {
            name: 'Corrosion Distribution',
            series: [0, 0, 0],
            labels: ['Light', 'Moderate', 'Heavy'],
            colors: ['#0EA8B2', '#FFDE53', '#F55656'],
          },
        ],
      };

      const vesselChart = {
        name: 'Vessel Condition',
        key: 'vessel',
        chartType: 'group',
        groupKey: 'Vessel',
        chartGroupData: [
          {
            name: 'Corroded vs Clean',
            series: [0, 0, 0],
            labels: ['Clean', 'Corroded', 'Remediated'],
            colors: ['#02B49C', '#F28705', '#799df8'],
          },
          {
            name: 'Corrosion Distribution',
            series: [0, 0, 0],
            labels: ['Light', 'Moderate', 'Heavy'],
            colors: ['#0EA8B2', '#FFDE53', '#F55656'],
          },
        ],
      };

      const partGroupCharts = [lineChart, vesselChart];

      const partClassCharts = [
        {
          name: 'Pipe Support Condition',
          key: 'Support',
          chartType: 'partClass',
          classKey: 'Support',
          chartGroupData: [
            {
              name: 'Corroded vs Clean',
              series: [0, 0, 0],
              labels: ['Clean', 'Corroded', 'Remediated'],
              colors: ['#02B49C', '#F28705', '#799df8'],
            },
            {
              name: 'Corrosion Distribution',
              series: [0, 0, 0],
              labels: ['Light', 'Moderate', 'Heavy'],
              colors: ['#0EA8B2', '#FFDE53', '#F55656'],
            },
          ],
        },
        {
          name: 'Flange Condition',
          key: 'Flange',
          chartType: 'partClass',
          classKey: 'Flange',
          chartGroupData: [
            {
              name: 'Corroded vs Clean',
              series: [0, 0, 0],
              labels: ['Clean', 'Corroded', 'Remediated'],
              colors: ['#02B49C', '#F28705', '#799df8'],
            },
            {
              name: 'Corrosion Distribution',
              series: [0, 0, 0],
              labels: ['Light', 'Moderate', 'Heavy'],
              colors: ['#0EA8B2', '#FFDE53', '#F55656'],
            },
          ],
        },
      ];

      const chartQuery = query.filter((x) => !chartsExcludeFilter.includes(x.key));
      // only update charts if the chartQuery has changed
      // i.e. if the table query corrosion filter changes, the chart doesn't need to be updated
      const chartsValid =
        this.activeChartQuery != null &&
        chartQuery.every((x) => this.activeChartQuery.includes(x)) &&
        this.activeChartQuery.every((x) => chartQuery.includes(x));
      this.activeChartQuery = chartQuery;

      if (this.metricTables.currentState === this.metricTables.currentStateEnum.aggregate) {
        const resultAggregated = await MetricController.getMetricAggregate(
          this.$route.query.id,
          'asset',
          2,
          query,
          aggregationKey
        );
        // set data for export
        this.tableRes = resultAggregated.data.map(formatAggregateFields(this.inspectionConfig));
      } else {
        const resultNonAggregated = await MetricController.getMetricChildren2(this.$route.query.id, 'asset', 2, query, {
          type: 1,
          'data.meta': 1,
          'data.metrics': 1,
          inspection_uuid: 1,
          'data.referring_images': 1,
          name: 1,
        });

        // set data for export
        this.tableRes = resultNonAggregated.data.map(
          formatNonAggregateFields(this.inspectionConfig, this.extraEquipmentText)
        );
      }
      if (!chartsValid) {
        const [resultGroupCounts, resultClassCounts] = await Promise.all([
          MetricController.getAssetCounts(this.$route.query.id, aggregationKey, chartQuery),
          MetricController.getPartCounts(this.$route.query.id, chartQuery),
        ]);

        const calculateCleanVsCorroded = (items) =>
          items.reduce(
            ({ clean, corroded, remediated }, item) => {
              if (get(item, '_id.corrosionCategory') === 0) {
                // if clean sum up remediated and non remediated separatly
                return get(item, '_id.remediated')
                  ? { clean, corroded, remediated: remediated + item.total }
                  : { clean: clean + item.total, corroded, remediated };
              }
              // if any corrosion (not equal to 0) sum up remediated and non remediated
              return { clean, corroded: corroded + item.total, remediated };
            },
            { clean: 0, corroded: 0, remediated: 0 }
          );

        const setChartData = (chart, chartData) => {
          const { clean, corroded, remediated } = calculateCleanVsCorroded(chartData);
          chart.chartGroupData[0].series[0] = clean;
          chart.chartGroupData[0].series[1] = corroded;
          chart.chartGroupData[0].series[2] = remediated;

          [1, 2, 3].forEach((f) => {
            chart.chartGroupData[1].series[f - 1] = chartData
              .filter((item) => get(item, '_id.corrosionCategory') === f)
              .reduce((value, item) => value + item.total, 0);
          });
        };

        partGroupCharts.forEach((chart) => {
          const chartData = resultGroupCounts.data.filter((f) => f._id.groupKey === chart.groupKey);
          setChartData(chart, chartData);
        });

        partClassCharts.forEach((chart) => {
          const chartData = resultClassCounts.data.filter((f) => f._id.class === chart.classKey);
          setChartData(chart, chartData);
        });

        this.pieCharts = [...partGroupCharts, ...partClassCharts];
      }
      return true;
    },
    resetFilters() {
      this.metricTables.currentState = this.metricTables.currentStateEnum.aggregate;
      this.updateFilterState([], [], true);

      // reset pie chart selection
      if (this.$refs.pieChart) {
        this.$refs.pieChart.forEach(({ deSelectSegment }) => deSelectSegment());
      }
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
.flex-display {
  display: flex;
  align-items: center;
}
</style>
